import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import LeadEmailDrawer from '../components/LeadEmailDrawer';
import Icon from '../components/Icon';

const STAGES = ['new', 'contacted', 'meeting_booked', 'proposal_sent', 'won', 'lost', 'cold'];
const STAGE_LABELS = { new: 'Yeni', contacted: 'İletişime Geçildi', meeting_booked: 'Toplantı Planlandı', proposal_sent: 'Teklif Gönderildi', won: 'Kazanıldı', lost: 'Kaybedildi', cold: 'Soğuk' };
const STAGE_COLORS = { new: '#D4FF3A', contacted: '#A78BFA', meeting_booked: '#F2B84B', proposal_sent: '#FF8A3D', won: '#34D399', lost: '#FF6B5B', cold: '#74746D' };
const SERVICES = ['Amazon Full Service Management', 'Amazon Advertising', 'Shopify', 'Meta Ads'];

function MailBadge({ lead }) {
  if (lead.email_received) return <span className="badge" style={{ background: '#D4FF3A22', color: '#D4FF3A', fontWeight: 600 }}><Icon name="inbox" size={12} /> Cevap Geldi</span>;
  if (lead.email_opened)   return <span className="badge" style={{ background: '#FF8A3D22', color: '#FF8A3D', fontWeight: 600 }}><Icon name="eye" size={12} /> Açıldı</span>;
  if (lead.email_sent)     return <span className="badge" style={{ background: '#34D39922', color: '#34D399', fontWeight: 600 }}>✓ Gönderildi</span>;
  return <span className="badge" style={{ background: '#9A9A9222', color: '#74746D', fontWeight: 600 }}>○ Bekliyor</span>;
}

const isMailSent = (l) => !!(l.email_sent || l.email_opened || l.email_received);

const SOURCE_BADGES = {
  apollo:            { label: 'Apollo',    color: '#D4FF3A', icon: null },
  amazon_pipeline:   { label: 'Amazon',    color: '#34D399', icon: null },
  hatb_apollo:       { label: 'Hat B',     color: '#F2B84B', icon: null },
  pattern_recovered: { label: 'Pattern',   color: '#9A9A92', icon: null },
};

const BIZ_TAGS = [
  { key: 'b2b',         label: 'B2B',       color: '#D4FF3A' },
  { key: 'b2c',         label: 'B2C',       color: '#A78BFA' },
  { key: 'd2c',         label: 'D2C',       color: '#ec4899' },
  { key: 'wholesale',   label: 'Wholesale', color: '#F2B84B' },
  { key: 'retail',      label: 'Retail',    color: '#10b981' },
  { key: 'ecommerce',   label: 'E-Commerce',color: '#14b8a6', match: (s) => s.includes('e-commerce') || s.includes('ecommerce') },
  { key: 'marketplace', label: 'Marketplace',color: '#74746D' },
];

const extractBizTags = (keywords) => {
  if (!keywords) return [];
  const s = keywords.toLowerCase();
  return BIZ_TAGS.filter(t => (t.match ? t.match(s) : s.includes(t.key)));
};

export default function Leads() {
  const { can } = useAuth();
  const [leads,         setLeads]         = useState([]);
  const [inactiveLeads, setInactiveLeads] = useState([]); // is_active=false olanlar
  const [view,          setView]          = useState('table');
  const [emailFilter,   setEmailFilter]   = useState('all');
  const [search,        setSearch]        = useState('');
  const [showForm,      setShowForm]      = useState(false);
  const [selectedLead,  setSelectedLead]  = useState(null);
  const [form, setForm] = useState({ company_name: '', contact_name: '', email: '', phone: '', source: '', notes: '', services: [] });
  const [loading,       setLoading]       = useState(true);
  const [expanded,      setExpanded]      = useState({});
  const [expandedInactive, setExpandedInactive] = useState(new Set()); // "..." açık şirketler
  const [drawerLead,    setDrawerLead]    = useState(null);
  const [drawerMode,    setDrawerMode]    = useState('view');

  // ── Çoklu silme ──────────────────────────────────────────────────────────────
  const [selectMode,       setSelectMode]       = useState(false);
  const [selectedIds,      setSelectedIds]      = useState(new Set());
  const [showDeleteConfirm,setShowDeleteConfirm]= useState(false);
  const [deleting,         setDeleting]         = useState(false);
  const [unlockedIds,      setUnlockedIds]      = useState(new Set()); // kilidi açılan gizli leadler

  const [sourceFilter,   setSourceFilter]   = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy,         setSortBy]         = useState('created_at_desc');

  // Apollo import
  const [showImport,   setShowImport]   = useState(false);
  const [importing,    setImporting]    = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    try {
      // ?all=1 → aktif + gizli hepsini getirir; is_active alanıyla ayrıştırıyoruz
      const res = await api.get('/leads?all=1');
      const all = res.data;
      setLeads(all.filter(l => l.is_active !== false));
      setInactiveLeads(all.filter(l => l.is_active === false));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Gizli leadleri şirkete göre grupla
  const inactiveByCompany = useMemo(() => {
    const map = new Map();
    for (const l of inactiveLeads) {
      const key = (l.company_name || '—').trim();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    }
    return map;
  }, [inactiveLeads]);

  const toggleCompany = (company) =>
    setExpanded(prev => ({ ...prev, [company]: !prev[company] }));

  const toggleInactive = (company) => {
    setExpandedInactive(prev => {
      const next = new Set(prev);
      next.has(company) ? next.delete(company) : next.add(company);
      return next;
    });
  };


  const openDrawer = (e, lead, mode) => {
    e.stopPropagation();
    setDrawerLead(lead);
    setDrawerMode(mode);
  };

  // ── Çoklu seçim ──────────────────────────────────────────────────────────────
  const toggleSelectMode = () => {
    setSelectMode(p => !p);
    setSelectedIds(new Set());
  };

  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      await api.delete('/leads/bulk', { data: { ids: [...selectedIds] } });
      setSelectedIds(new Set());
      setSelectMode(false);
      setShowDeleteConfirm(false);
      fetchLeads();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, notes: form.notes + (form.services.length ? `\n\nHizmetler: ${form.services.join(', ')}` : '') };
      await api.post('/leads', payload);
      setForm({ company_name: '', contact_name: '', email: '', phone: '', source: '', notes: '', services: [] });
      setShowForm(false);
      fetchLeads();
    } catch (err) { console.error(err); }
  };

  const updateStage = async (id, stage) => {
    try { await api.put(`/leads/${id}`, { stage }); fetchLeads(); }
    catch (err) { console.error(err); }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, source, destination } = result;
    if (source.droppableId === destination.droppableId) return;
    const leadId = parseInt(draggableId);
    const newStage = destination.droppableId;
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, stage: newStage } : l));
    try { await api.put(`/leads/${leadId}`, { stage: newStage }); }
    catch (err) { console.error(err); fetchLeads(); }
  };

  const toggleService = (service) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.includes(service) ? prev.services.filter(s => s !== service) : [...prev.services, service]
    }));
  };

  const handleImportApollo = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await api.post('/leads/import-apollo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      fetchLeads();
    } catch (err) {
      setImportResult({ error: true, message: err.response?.data?.message || 'Yükleme sırasında hata oluştu' });
    } finally { setImporting(false); }
  };

  const closeImportModal = () => { setShowImport(false); setSelectedFile(null); setImportResult(null); setImporting(false); };

  const filtered = leads.filter(l => {
    const matchesSearch =
      l.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      l.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
      l.email?.toLowerCase().includes(search.toLowerCase());
    const matchesEmail =
      emailFilter === 'all'      ? true :
      emailFilter === 'received' ? !!l.email_received :
      emailFilter === 'opened'   ? (!!l.email_opened && !l.email_received) :
      emailFilter === 'sent'     ? (!!l.email_sent && !l.email_opened && !l.email_received) :
      !l.email_sent;
    const matchesSource =
      sourceFilter === 'all' ? true : l.source === sourceFilter;
    const matchesCategory =
      categoryFilter === 'all' ? true : (l.category || '').toLowerCase().includes(categoryFilter.toLowerCase());
    return matchesSearch && matchesEmail && matchesSource && matchesCategory;
  });

  const companyGroups = useMemo(() => {
    const map = new Map();
    for (const l of filtered) {
      const key = (l.company_name || '—').trim() || '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    }
    return Array.from(map.entries())
      .map(([company, items]) => {
        const totalEmails = items.filter(i => i.email && i.email.trim()).length;
        const sentCount   = items.filter(isMailSent).length;
        const tagKeys = new Set();
        items.forEach(i => extractBizTags(i.keywords).forEach(t => tagKeys.add(t.key)));
        const tags = BIZ_TAGS.filter(t => tagKeys.has(t.key));
        const sources = [...new Set(items.map(i => i.source).filter(Boolean))];
        const categories = [...new Set(items.map(i => i.category).filter(Boolean))];
        const firstCreated = items.reduce((min, i) => i.created_at && new Date(i.created_at) < min ? new Date(i.created_at) : min, new Date());
        return { company, items, people: items.length, totalEmails, sentCount, tags, sources, categories, firstCreated };
      })
      .sort((a, b) => {
        if (sortBy === 'company_asc')      return a.company.localeCompare(b.company, 'tr');
        if (sortBy === 'company_desc')     return b.company.localeCompare(a.company, 'tr');
        if (sortBy === 'people_desc')      return b.people - a.people || a.company.localeCompare(b.company, 'tr');
        if (sortBy === 'mail_reply_first') return (b.items.some(i => i.email_received) ? 1 : 0) - (a.items.some(i => i.email_received) ? 1 : 0);
        if (sortBy === 'created_at_desc')  return Math.max(...b.items.map(i => new Date(i.created_at||0))) - Math.max(...a.items.map(i => new Date(i.created_at||0)));
        if (sortBy === 'created_at_asc')   return Math.min(...a.items.map(i => new Date(i.created_at||0))) - Math.min(...b.items.map(i => new Date(i.created_at||0)));
        return b.people - a.people;
      });
  }, [filtered]);

  // ── Person row (aktif veya gizli) ────────────────────────────────────────────
  const PersonRow = ({ lead, isHidden = false }) => {
    const location = [lead.city, lead.country].filter(Boolean).join(', ');
    const mailUnlocked = isHidden && unlockedIds.has(lead.id);

    const toggleUnlock = (e) => {
      e.stopPropagation();
      setUnlockedIds(prev => {
        const next = new Set(prev);
        next.has(lead.id) ? next.delete(lead.id) : next.add(lead.id);
        return next;
      });
    };

    return (
      <tr
        key={lead.id}
        onClick={() => !selectMode && !isHidden && setSelectedLead(lead)}
        className={!selectMode && !isHidden ? 'data-table__row--clickable' : undefined}
        style={isHidden ? { opacity: 0.45 } : undefined}
      >
        {/* İsim + seçim/kilit */}
        <td style={{ fontWeight: 600, paddingLeft: 40, width: '18%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {selectMode && !isHidden && (
              <input
                type="checkbox"
                checked={selectedIds.has(lead.id)}
                onChange={(e) => toggleSelect(e, lead.id)}
                onClick={(e) => e.stopPropagation()}
                style={{ accentColor: '#FF6B5B', width: 15, height: 15, flexShrink: 0 }}
              />
            )}
            {/* kilit ikonu tıklanınca mail butonu açılır/kapanır, lead yerinde kalır */}
            {isHidden && (
              <button
                onClick={toggleUnlock}
                title={mailUnlocked ? 'Gizle' : 'Mail seçeneklerini göster'}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: 0, fontSize: 12, lineHeight: 1, flexShrink: 0,
                  opacity: mailUnlocked ? 1 : 0.7,
                }}
              >
                <Icon name={mailUnlocked ? 'unlock' : 'lock'} size={13} />
              </button>
            )}
            {lead.contact_name || '—'}
          </div>
        </td>
        <td style={{ width: '22%' }}><span className="text-accent">{lead.email || '—'}</span></td>
        <td className="text-muted" style={{ width: '15%' }}>{location || '—'}</td>
        <td className="text-muted" style={{ width: '15%' }}>{lead.title || '—'}</td>
        <td style={{ width: '12%' }}><MailBadge lead={lead} /></td>
        <td style={{ width: '7%' }}>{lead.source || '—'}</td>
        <td style={{ fontSize: 11, color: '#74746D', width: '10%' }}>
          {lead.created_at ? new Date(lead.created_at).toLocaleDateString('tr-TR', { day:'2-digit', month:'short' }) : '—'}
        </td>
        <td style={{ width: '9%' }}>
          {/* Aktif leadlerde her zaman, gizli leadlerde sadece kilit açıldıysa */}
          {(!isHidden || mailUnlocked) && (
            lead.email_sent ? (
              <button className="btn btn--secondary" style={{ padding: '4px 10px', fontSize: 12 }}
                onClick={(e) => openDrawer(e, lead, 'view')}><Icon name="mail" size={13} /> Gör</button>
            ) : (
              <button className="btn btn--secondary" style={{ padding: '4px 10px', fontSize: 12 }}
                onClick={(e) => openDrawer(e, lead, 'compose')}><Icon name="send" size={13} /> Gönder</button>
            )
          )}
        </td>
      </tr>
    );
  };

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="page-subtitle">Potansiyel müşteriler ve satış süreci</p>
        </div>
        <div className="page-actions">
          {/* Sıralama */}
          <select
            className="btn btn--secondary"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ paddingRight: 32 }}
          >
            <option value="created_at_desc">↓ En Yeni</option>
            <option value="created_at_asc">↑ En Eski</option>
            <option value="company_asc">A→Z Şirket</option>
            <option value="company_desc">Z→A Şirket</option>
            <option value="people_desc">Kişi Sayısı</option>
            <option value="mail_reply_first">Cevap Önce</option>
          </select>

          {/* Kaynak filtresi */}
          <select
            className="btn btn--secondary"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            style={{ paddingRight: 32 }}
          >
            <option value="all">Tüm Kaynaklar</option>
            <option value="apollo">Apollo</option>
            <option value="amazon_pipeline">Amazon</option>
            <option value="hatb_apollo">Hat B</option>
            <option value="pattern_recovered">Pattern</option>
          </select>

          {/* Kategori filtresi */}
          <select
            className="btn btn--secondary"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{ paddingRight: 32 }}
          >
            <option value="all">Tüm Kategoriler</option>
            <option value="health">Health</option>
            <option value="beauty">Beauty</option>
            <option value="pet">Pet</option>
            <option value="grocery">Grocery</option>
            <option value="sports">Sports</option>
            <option value="home">Home & Kitchen</option>
            <option value="newrel">🆕 New Releases</option>
          </select>

          {/* E-posta filtre */}
          <select className="btn btn--secondary" value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} style={{ paddingRight: 32 }}>
            <option value="all">Tüm E-posta Durumu</option>
            <option value="received">Cevap Geldi</option>
            <option value="opened">Açıldı</option>
            <option value="sent">✓ Gönderildi</option>
            <option value="not_sent">○ Bekliyor</option>
          </select>

          {/* Arama */}
          <div className="search-bar">
            <span className="search-bar__icon"><Icon name="search" size={15} /></span>
            <input className="search-bar__input" placeholder="Lead ara..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>

          {/* Çoklu silme butonu — sadece admin (backend de sadece admin'e izin verir) */}
          {!can('leads.bulk_delete') ? null : selectMode ? (
            <>
              <button
                className="btn btn--primary"
                style={{ background: '#FF6B5B', borderColor: '#FF6B5B', opacity: selectedIds.size === 0 ? 0.5 : 1 }}
                disabled={selectedIds.size === 0}
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Icon name="trash" size={14} /> {selectedIds.size} Seçiliyi Sil
              </button>
              <button className="btn btn--secondary" onClick={toggleSelectMode}>İptal</button>
            </>
          ) : (
            <button className="btn btn--secondary" onClick={toggleSelectMode} title="Toplu silme moduna geç">
              <Icon name="trash" size={14} /> Sil
            </button>
          )}

          <button className="btn btn--secondary" onClick={() => setView(view === 'table' ? 'kanban' : 'table')}>
            {view === 'table' ? <><Icon name="kanban" size={14} /> Kanban</> : <><Icon name="table" size={14} /> Tablo</>}
          </button>
          <button className="btn btn--secondary" onClick={() => setShowImport(true)}><Icon name="upload" size={14} /> Apollo CSV</button>
          <button className="btn btn--primary" onClick={() => setShowForm(true)}>+ Yeni Lead</button>
        </div>
      </div>

      {loading ? (
        <div className="page-loading">Yükleniyor...</div>
      ) : view === 'table' ? (
        <div className="card" style={{ overflow: 'hidden' }}>
          {/* Şirket sayısı bilgisi */}
          <div style={{ padding: '10px 16px 6px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              <span style={{ fontWeight: 700, color: 'var(--text)' }}>{companyGroups.length}</span> şirket
              &nbsp;·&nbsp;
              <span style={{ fontWeight: 700, color: 'var(--text)' }}>{filtered.length}</span> aktif lead
              {inactiveLeads.length > 0 && (
                <span style={{ color: '#74746D' }}>&nbsp;·&nbsp;{inactiveLeads.length} gizli</span>
              )}
              {(sourceFilter !== 'all' || categoryFilter !== 'all' || emailFilter !== 'all') && (
                <span style={{ color: '#D4FF3A', fontWeight: 600 }}>
                  &nbsp;·&nbsp;<Icon name="filter" size={12} /> Filtreleniyor
                </span>
              )}
            </span>
            {selectMode && selectedIds.size > 0 && (
              <span style={{ fontSize: 12, color: '#FF6B5B', fontWeight: 600 }}>
                {selectedIds.size} lead seçildi
              </span>
            )}
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '34%' }}>Şirket</th>
                <th style={{ width: '22%' }}>Tür</th>
                <th style={{ width: '12%' }}>Kişi</th>
                <th style={{ width: '15%' }}>Eklenme</th>
                <th style={{ width: '16%' }}>Mail Gönderim</th>
              </tr>
            </thead>
            <tbody>
              {companyGroups.map(group => {
                const isOpen       = !!expanded[group.company];
                const hiddenLeads  = inactiveByCompany.get(group.company) || [];
                const inactiveOpen = expandedInactive.has(group.company);

                return (
                  <React.Fragment key={group.company}>
                    {/* ── Şirket özet satırı ── */}
                    <tr
                      onClick={() => toggleCompany(group.company)}
                      className="data-table__row--clickable"
                      style={{ background: 'rgba(148,163,184,0.06)' }}
                    >
                      <td style={{ fontWeight: 700 }}>
                        <span style={{
                          display: 'inline-block', width: 16, marginRight: 8, color: '#9A9A92',
                          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s'
                        }}>▶</span>
                        {group.company}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                          {group.sources.map(src => {
                            const b = SOURCE_BADGES[src] || { label: src, color: '#74746D', icon: '●' };
                            return (
                              <span key={src} className="badge" style={{ background: b.color + '22', color: b.color, fontWeight: 600, fontSize: 11 }}>
                                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 4, background: b.color, marginRight: 4 }} />{b.label}
                              </span>
                            );
                          })}
                          {group.categories.slice(0,1).map(cat => (
                            <span key={cat} className="badge" style={{ background: '#0E0F11', color: '#9A9A92', fontWeight: 500, fontSize: 10, border: '1px solid #17191C' }}>
                              {cat.replace('Health & Household','Health').replace('Sports & Outdoors','Sports').replace('Home & Kitchen','Home').replace('Pet Supplies','Pet')}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span className="badge" style={{ background: '#D4FF3A22', color: '#D4FF3A', fontWeight: 600 }}>
                          <Icon name="user" size={12} /> {group.people} kişi
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#74746D' }}>
                        {group.firstCreated ? new Date(group.firstCreated).toLocaleDateString('tr-TR', { day:'2-digit', month:'short', year:'2-digit' }) : '—'}
                      </td>
                      <td>
                        {(() => {
                          const ratio = group.totalEmails > 0 ? group.sentCount / group.totalEmails : 0;
                          const color = group.sentCount === 0 ? '#74746D' : ratio >= 1 ? '#34D399' : '#F2B84B';
                          return (
                            <span className="badge" style={{ background: color + '22', color, fontWeight: 700 }}>
                              <Icon name="mail" size={12} /> {group.sentCount}/{group.totalEmails} gönderildi
                            </span>
                          );
                        })()}
                      </td>
                    </tr>

                    {/* ── Açık detay bölümü ── */}
                    {isOpen && (
                      <tr>
                        <td colSpan="5" style={{ padding: 0, background: 'rgba(15,23,42,0.35)' }}>
                          <table className="data-table" style={{ margin: 0 }}>
                            <thead>
                              <tr>
                                <th style={{ width: '13%', paddingLeft: 40 }}>Kişi</th>
                                <th style={{ width: '18%' }}>E-posta</th>
                                <th style={{ width: '13%' }}>Konum</th>
                                <th style={{ width: '13%' }}>Ünvan</th>
                                <th style={{ width: '11%' }}>Mail Durumu</th>
                                <th style={{ width: '7%' }}>Kaynak</th>
                                <th style={{ width: '10%' }}>Tarih</th>
                                <th style={{ width: '9%' }}>Mail</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* Aktif leadler */}
                              {group.items.map(lead => (
                                <PersonRow key={lead.id} lead={lead} />
                              ))}

                              {/* "..." — gizli lead açma butonu */}
                              {hiddenLeads.length > 0 && (
                                <tr>
                                  <td colSpan="8" style={{ textAlign: 'center', padding: '8px 0' }}>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleInactive(group.company); }}
                                      style={{
                                        background: 'none', border: '1px dashed #2A2D32',
                                        color: '#74746D', cursor: 'pointer', borderRadius: 6,
                                        padding: '4px 18px', fontSize: 13, letterSpacing: 2,
                                        transition: 'all 0.15s'
                                      }}
                                      title={`${hiddenLeads.length} gizlenmiş lead`}
                                    >
                                      {inactiveOpen
                                        ? `▲ Gizle (${hiddenLeads.length} kişi)`
                                        : `••• ${hiddenLeads.length} ilgisiz lead`}
                                    </button>
                                  </td>
                                </tr>
                              )}

                              {/* Gizli leadler (açıldıysa) */}
                              {inactiveOpen && hiddenLeads.map(lead => (
                                <PersonRow key={lead.id} lead={lead} isHidden />
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {companyGroups.length === 0 && (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: 32 }} className="text-muted">Lead bulunamadı</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* Kanban */
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="kanban">
            {STAGES.map(stage => {
              const stageLeads = filtered.filter(l => l.stage === stage);
              return (
                <Droppable droppableId={stage} key={stage}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="kanban__column"
                      style={{ background: snapshot.isDraggingOver ? STAGE_COLORS[stage] + '11' : undefined, transition: 'background 0.2s' }}>
                      <div className="kanban__column-header">
                        <span className="badge" style={{ background: STAGE_COLORS[stage] + '22', color: STAGE_COLORS[stage] }}>{STAGE_LABELS[stage]}</span>
                        <span className="text-muted text-sm">{stageLeads.length}</span>
                      </div>
                      {stageLeads.map((lead, index) => (
                        <Draggable key={lead.id} draggableId={String(lead.id)} index={index}>
                          {(provided, snapshot) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                              className="kanban__card"
                              onClick={() => !snapshot.isDragging && setSelectedLead(lead)}
                              style={{ ...provided.draggableProps.style, opacity: snapshot.isDragging ? 0.8 : 1, cursor: snapshot.isDragging ? 'grabbing' : 'grab' }}>
                              <div className="kanban__card-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.company_name}</span>
                                {lead.email_received ? <span style={{ color: '#D4FF3A', fontSize: 14 }}><Icon name="inbox" size={14} /></span>
                                  : lead.email_opened ? <span style={{ color: '#FF8A3D', fontSize: 14 }}><Icon name="eye" size={14} /></span>
                                  : lead.email_sent   ? <span style={{ color: '#34D399', fontSize: 14 }}>✓</span>
                                  : null}
                              </div>
                              <div className="kanban__card-subtitle">{lead.contact_name || '—'}</div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>
      )}

      {/* ── Silme Onay Modalı ───────────────────────────────────────────────── */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => !deleting && setShowDeleteConfirm(false)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
              <div style={{ marginBottom: 12, color: '#FF6B5B' }}><Icon name="trash" size={40} stroke={1.5} /></div>
              <h2 className="modal__title" style={{ marginBottom: 8 }}>Emin misiniz?</h2>
              <p className="text-muted text-sm" style={{ margin: '0 0 24px' }}>
                <strong style={{ color: '#FF6B5B' }}>{selectedIds.size} lead</strong> kalıcı olarak silinecek.
                Bu işlem geri alınamaz.
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  className="btn btn--primary"
                  style={{ flex: 1, background: '#FF6B5B', borderColor: '#FF6B5B', opacity: deleting ? 0.6 : 1 }}
                  onClick={handleBulkDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Siliniyor...' : 'Evet, Sil'}
                </button>
                <button
                  className="btn btn--secondary"
                  style={{ flex: 1 }}
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lead Mail Drawer */}
      {drawerLead && (
        <LeadEmailDrawer
          key={`${drawerLead.id}-${drawerMode}`}
          lead={drawerLead}
          mode={drawerMode}
          onClose={() => setDrawerLead(null)}
          onChanged={fetchLeads}
        />
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="modal-overlay" onClick={() => setSelectedLead(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">{selectedLead.company_name}</h2>
              <button className="modal__close" onClick={() => setSelectedLead(null)}>×</button>
            </div>
            <div className="modal__grid">
              {[
                { label: 'İletişim Kişisi', value: selectedLead.contact_name },
                { label: 'Email', value: selectedLead.email },
                { label: 'Mail Durumu', value: selectedLead.email_received ? `Cevap Geldi` : selectedLead.email_opened ? `Açıldı` : selectedLead.email_sent ? `✓ Gönderildi` : '○ Henüz gönderilmedi' },
                { label: 'Telefon', value: selectedLead.phone },
                { label: 'Kaynak', value: selectedLead.source },
                { label: 'Kategori',   value: selectedLead.category },
                { label: 'Şehir',      value: selectedLead.city },
                { label: 'Ülke',       value: selectedLead.country },
                { label: 'LinkedIn',   value: selectedLead.linkedin_url },
                { label: 'Website',    value: selectedLead.company_website },
                { label: 'Aşama', value: STAGE_LABELS[selectedLead.stage] },
                { label: 'Takip Tarihi', value: selectedLead.next_follow_up ? new Date(selectedLead.next_follow_up).toLocaleDateString('tr-TR') : '—' },
              ].map(item => (
                <div key={item.label} className="modal__field">
                  <p className="modal__field-label">{item.label}</p>
                  <p className="modal__field-value">{item.value || '—'}</p>
                </div>
              ))}
            </div>
            {selectedLead.notes && (
              <div className="modal__field" style={{ marginTop: 16 }}>
                <p className="modal__field-label">Notlar</p>
                <p className="modal__field-value" style={{ whiteSpace: 'pre-line' }}>{selectedLead.notes}</p>
              </div>
            )}
            <button className="btn btn--secondary" style={{ width: '100%', marginTop: 20 }} onClick={() => setSelectedLead(null)}>Kapat</button>
          </div>
        </div>
      )}

      {/* New Lead Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal__title" style={{ marginBottom: 24 }}>Yeni Lead Ekle</h2>
            <form onSubmit={handleSubmit}>
              {[
                { key: 'company_name', label: 'Şirket Adı *', required: true },
                { key: 'contact_name', label: 'İletişim Kişisi' },
                { key: 'email', label: 'Email' },
                { key: 'phone', label: 'Telefon' },
                { key: 'source', label: 'Kaynak (Referans, LinkedIn, vs.)' },
              ].map(field => (
                <div key={field.key} className="form-group">
                  <label className="form-label">{field.label}</label>
                  <input className="form-input" value={form[field.key]} onChange={(e) => setForm({ ...form, [field.key]: e.target.value })} required={field.required} />
                </div>
              ))}
              <div className="form-group">
                <label className="form-label">İlgilendiği Hizmetler</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {SERVICES.map(service => (
                    <label key={service} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.services.includes(service)} onChange={() => toggleService(service)} style={{ accentColor: '#D4FF3A' }} />
                      <span style={{ color: '#F2EFE6', fontSize: 14 }}>{service}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notlar</label>
                <textarea className="form-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} style={{ resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="submit" className="btn btn--primary" style={{ flex: 1 }}>Kaydet</button>
                <button type="button" className="btn btn--secondary" style={{ flex: 1 }} onClick={() => setShowForm(false)}>İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Apollo CSV Import Modal */}
      {showImport && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2 className="modal__title">Apollo CSV Import</h2>
              <button className="modal__close" onClick={closeImportModal}>×</button>
            </div>
            {!importResult && (
              <form onSubmit={handleImportApollo}>
                <p className="text-muted text-sm" style={{ marginBottom: 16, lineHeight: 1.5 }}>
                  Apollo'dan export ettiğin CSV dosyasını seç. Aynı kişi (Apollo Contact Id) varsa atlanacak, yenileri eklenecek.
                </p>
                <div className="form-group">
                  <label className="form-label">CSV Dosyası</label>
                  <input type="file" accept=".csv" onChange={(e) => setSelectedFile(e.target.files[0])} className="form-input" />
                  {selectedFile && <p style={{ color: '#34D399', fontSize: 12, margin: '6px 0 0' }}>✓ {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</p>}
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button type="submit" disabled={!selectedFile || importing} className="btn btn--primary"
                    style={{ flex: 1, opacity: (!selectedFile || importing) ? 0.5 : 1 }}>
                    {importing ? 'Yükleniyor...' : 'Yükle ve İçe Aktar'}
                  </button>
                  <button type="button" onClick={closeImportModal} className="btn btn--secondary" style={{ flex: 1 }}>İptal</button>
                </div>
              </form>
            )}
            {importResult && !importResult.error && (
              <div>
                <div className="card" style={{ padding: 20, marginBottom: 16, border: '1px solid #34D399' }}>
                  <p style={{ color: '#34D399', fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>✓ Import Tamamlandı</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div><p className="text-muted text-sm" style={{ margin: '0 0 4px' }}>Yeni</p><p style={{ color: '#34D399', fontSize: 22, fontWeight: 700, margin: 0 }}>{importResult.inserted}</p></div>
                    <div><p className="text-muted text-sm" style={{ margin: '0 0 4px' }}>Atlandı</p><p style={{ color: '#F2B84B', fontSize: 22, fontWeight: 700, margin: 0 }}>{importResult.skipped}</p></div>
                    <div><p className="text-muted text-sm" style={{ margin: '0 0 4px' }}>Hata</p><p style={{ color: '#FF6B5B', fontSize: 22, fontWeight: 700, margin: 0 }}>{importResult.errors}</p></div>
                  </div>
                  {importResult.prioritized && (
                    <p className="text-muted text-sm" style={{ margin: '12px 0 0' }}>
                      Önceliklendirme: <strong style={{ color: '#34D399' }}>{importResult.prioritized.active} aktif</strong>
                      {importResult.prioritized.deactivated > 0 && <>, <strong style={{ color: '#74746D' }}>{importResult.prioritized.deactivated} gizlendi</strong></>}
                    </p>
                  )}
                </div>
                <button onClick={closeImportModal} className="btn btn--primary" style={{ width: '100%' }}>Tamam</button>
              </div>
            )}
            {importResult && importResult.error && (
              <div>
                <div className="card" style={{ padding: 20, marginBottom: 16, border: '1px solid #FF6B5B' }}>
                  <p style={{ color: '#FF6B5B', fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>✕ Hata</p>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>{importResult.message}</p>
                </div>
                <button onClick={() => setImportResult(null)} className="btn btn--primary" style={{ width: '100%' }}>Tekrar Dene</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
